import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./Summary.css";

// @ts-ignore
import FilterPanelLevel1, {
  FILTERS_L1_DEFAULTS,
  type FiltersL1,
} from "../Filter/FilterLevel1/FilterPanelLevel1.tsx";
// @ts-ignore
import SummaryCharts from "./SummaryCharts/SummaryCharts.tsx";
// @ts-ignore
import Pagination from "../Pagination/Pagination.tsx";
// @ts-ignore
import Title from "../Title/Title.tsx";
// @ts-ignore
import { api, setApiBase, ping } from "../../api/client.tsx";
// @ts-ignore
import { Loader } from "../../Loading/Loading.tsx";
// @ts-ignore
import SummaryBoxChart from "./SummaryBoxCharts/SummaryBoxChart.tsx";
// @ts-ignore
import Breadcrumbs from "../BreadcrumbNav/Breadcrumbs.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";

type RepoRow = {
  version: string;
  creation_time?: string | null;

  layer_count?: number | null;
  size_mb?: number | null;
  package_count?: number | null;
  previous_version?: string | null;
  digest?: string | null;

  total_total?: number | null;
  low_total?: number | null;
  medium_total?: number | null;
  high_total?: number | null;
  critical_total?: number | null;

  total_new?: number | null;
  low_new?: number | null;
  medium_new?: number | null;
  high_new?: number | null;
  critical_new?: number | null;

  total_persisting?: number | null;
  low_persisting?: number | null;
  medium_persisting?: number | null;
  high_persisting?: number | null;
  critical_persisting?: number | null;

  total_obsolete?: number | null;
  low_obsolete?: number | null;
  medium_obsolete?: number | null;
  high_obsolete?: number | null;
  critical_obsolete?: number | null;

  lifespan_total?: number | null;
  lifespan_low?: number | null;
  lifespan_medium?: number | null;
  lifespan_high?: number | null;
  lifespan_critical?: number | null;

  total_chains?: number | null;
  package?: string;
};

const dash = (v: any) => (v === null || v === undefined || v === "" ? "—" : v);

function isAbortError(err: any): boolean {
  if (!err) return false;
  const name = String(err.name || "").toLowerCase();
  const msg = String(err.message || "").toLowerCase();
  return (
    name === "aborterror" ||
    msg.includes("aborted") ||
    msg.includes("canceled") ||
    msg.includes("cancelled")
  );
}

function QMark({ label, tip }: { label: string; tip: string }) {
  return (
    <button
      type="button"
      className="qmark"
      aria-label={label}
      data-tip={tip}
      onClick={(e) => e.preventDefault()}
    >
      <span aria-hidden>?</span>
    </button>
  );
}

const COL_DESC = {
  version: "Version/tag of the current image.",
  updated: "Timestamp of when this version was last updated (YYYY-MM-DD).",
  layers: "Number of layers containing CVEs in the image version.",
  total:
    "Vulnerability count in the image version with severity distribution (C: Critical, H: High, M: Medium, L: Low).",
  persisted:
    "Persisted vulnerability count from the previous version with severity distribution (C: Critical, H: High, M: Medium, L: Low).",
  added:
    "Added vulnerability count compared to the previous version with severity distribution (C: Critical, H: High, M: Medium, L: Low).",
  deleted:
    "Deleted vulnerability count compared to the previous version with severity distribution (C: Critical, H: High, M: Medium, L: Low).",
  lifespan:
    "Lifespan (how long a CVE lasts) of vulnerabilities in this image version, with severity distribution (C: Critical, H: High, M: Medium, L: Low).",
  chains: "Total number of CVE chains in the image version.",
  size: "Total size of the image version (in MB).",
  pkgs: "Number of vulnerable packages in the image version.",
  prev: "The version that was deployed immediately before this one.",
  digest: "SHA256 digest of the container image content.",
};

const SEARCHABLE_COLUMNS = [
  { key: "version", label: "Version" },
  { key: "creation_time", label: "Last Updated" },
  { key: "layer_count", label: "Layers" },
  { key: "total_total", label: "Total Vulnerabilities" },
  { key: "total_persisting", label: "Persisted Vulnerabilities" },
  { key: "total_new", label: "Added Vulnerabilities" },
  { key: "total_obsolete", label: "Deleted Vulnerabilities" },
  { key: "lifespan_total", label: "Average Vulnerability Lifespan" },
  { key: "total_chains", label: "CVE Chains" },
  { key: "size_mb", label: "Size (MB)" },
  { key: "package_count", label: "Vulnerable Packages" },
  { key: "previous_version", label: "Previous Version" },
  { key: "digest", label: "Image Digest" },
];

const NUMERIC_KEYS = new Set<string>([
  "layer_count",
  "size_mb",
  "package_count",
  "total_total",
  "total_persisting",
  "total_new",
  "total_obsolete",
  "lifespan_total",
  "total_chains",
]);

const getColumnLabel = (key: string) =>
  SEARCHABLE_COLUMNS.find((c) => c.key === key)?.label ?? key;

function Summary() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const qsApi = new URLSearchParams(window.location.search).get("api");
  useEffect(() => {
    if (qsApi) setApiBase(qsApi.replace(/\/+$/, ""));
  }, [qsApi]);

  // backend warmup (safe on unmount)
  useEffect(() => {
    let stopped = false;

    const warm = async () => {
      try {
        await ping("/statistics/overview");
      } catch (err) {
        if (isAbortError(err)) return;
        console.warn("Backend warmup failed", err);
      }
    };

    warm();

    const id = window.setInterval(() => {
      if (!stopped) warm();
    }, 4 * 60 * 1000);

    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, []);

  const routeKey = decodeURIComponent(params.repoPrefix ?? "");
  const pathKey = decodeURIComponent(
    location.pathname.replace("/versions/", "")
  );
  const baseKeyRaw = routeKey || pathKey;
  const repoKey = baseKeyRaw.replace(/___/g, "_");
  const isAll = repoKey.toLowerCase() === "all";

  const [selectedImage, setSelectedImage] = useState<string>("");
  const [rows, setRows] = useState<RepoRow[]>([]);
  const [total, setTotal] = useState(0);

  const [searchColumn, setSearchColumn] = useState<string>("");
  const [searchInput, setSearchInput] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");

  const [filters, setFilters] = useState<FiltersL1>(FILTERS_L1_DEFAULTS);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const justVersion = (v: string = ""): string => {
    if (!v) return v;
    const pref1 = `${repoKey}_`;
    const pref2 = `${repoKey}-`;
    if (!isAll && v.startsWith(pref1)) return v.slice(pref1.length);
    if (!isAll && v.startsWith(pref2)) return v.slice(pref2.length);
    const parts = v.split("_");
    if (!isAll && parts.length > 1 && parts[0] === repoKey) {
      return parts.slice(1).join("_");
    }
    return v;
  };

  useEffect(() => {
    const id = setTimeout(() => window.dispatchEvent(new Event("resize")), 0);
    return () => clearTimeout(id);
  }, [location.key]);

  // --------- LOAD LEVEL 1 TABLE ----------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!repoKey) return;

      try {
        const resp = await (api as any).level1.table({
          repo: isAll ? undefined : repoKey,
          page: currentPage,
          page_size: itemsPerPage,
        });

        if (cancelled) return;

        const formatted: RepoRow[] = (resp?.items || []).map((img: any) => ({
          version: String(img.version ?? ""),
          creation_time: img.creation_time ?? null,
          layer_count: img.layer_count ?? null,
          size_mb: img.size ?? img.size_mb ?? null,
          package_count: img.package_count ?? null,
          previous_version: img.previous_version ?? null,
          digest: img.digest ?? null,
          total_total: img.total_total ?? null,
          low_total: img.low_total ?? null,
          medium_total: img.medium_total ?? null,
          high_total: img.high_total ?? null,
          critical_total: img.critical_total ?? null,
          total_new: img.total_new ?? null,
          low_new: img.low_new ?? null,
          medium_new: img.medium_new ?? null,
          high_new: img.high_new ?? null,
          critical_new: img.critical_new ?? null,
          total_persisting: img.total_persisting ?? null,
          low_persisting: img.low_persisting ?? null,
          medium_persisting: img.medium_persisting ?? null,
          high_persisting: img.high_persisting ?? null,
          critical_persisting: img.critical_persisting ?? null,
          total_obsolete: img.total_obsolete ?? null,
          low_obsolete: img.low_obsolete ?? null,
          medium_obsolete: img.medium_obsolete ?? null,
          high_obsolete: img.high_obsolete ?? null,
          critical_obsolete: img.critical_obsolete ?? null,
          lifespan_total: img.lifespan_total ?? null,
          lifespan_low: img.lifespan_low ?? null,
          lifespan_medium: img.lifespan_medium ?? null,
          lifespan_high: img.lifespan_high ?? null,
          lifespan_critical: img.lifespan_critical ?? null,
          total_chains: img.total_chains ?? null,
          package: isAll ? img.image?.split("_")?.[0] ?? "image" : repoKey,
        }));

        setRows(formatted);
        setTotal(Number(resp?.total ?? formatted.length) || 0);

        if (formatted.length > 0) {
          const initialVersionOnly = justVersion(formatted[0].version);
          const base = isAll ? (formatted[0].package ?? "image") : repoKey;
          const initialId = `${base}_${initialVersionOnly}`.replace(/-/g, "_");
          setSelectedImage((prev) => prev || initialId);
        } else {
          setSelectedImage("");
        }
      } catch (err: any) {
        if (cancelled || isAbortError(err)) return;
        console.error("Error loading Level1 table", err);
        setRows([]);
        setTotal(0);
        setSelectedImage("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [repoKey, isAll, currentPage]);

  // reset page when filters/search change (frontend filters only)
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, activeSearchTerm, searchColumn]);

  const checkTuple = (t: [number | null, number | null], v?: number | null) => {
    if (v === null || v === undefined) return true;
    const [min, max] = t || [null, null];
    if (min === null && max === null) return true;
    if (min !== null && max !== null) return v === min;
    if (min !== null) return v >= min;
    return v <= (max as number);
  };

  const filtered = useMemo(() => {
    const f = filters;
    const col = searchColumn;
    const termRaw = activeSearchTerm.trim();
    const term = termRaw.toLowerCase();

    return rows.filter((r) => {
      if (!checkTuple(f.sizeMB, r.size_mb ?? null)) return false;

      if (!col || !termRaw) return true;

      let value: any = "";

      if (col === "version") {
        value = justVersion(r.version) || r.version;
      } else if (col === "creation_time") {
        if (r.creation_time) {
          try {
            value = new Date(r.creation_time).toISOString().slice(0, 10);
          } catch {
            value = String(r.creation_time);
          }
        } else {
          value = "";
        }
      } else {
        value = (r as any)[col];
      }

      if (NUMERIC_KEYS.has(col)) {
        const n = Number(termRaw);
        if (Number.isFinite(n) && typeof value === "number") {
          return value === n;
        }
        return String(value ?? "").toLowerCase().includes(term);
      }

      return String(value ?? "").toLowerCase().includes(term);
    });
  }, [rows, filters, searchColumn, activeSearchTerm]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / itemsPerPage));

  const handleRowClick = async (version: string) => {
    const versionOnly = justVersion(version);
    const fullImageId = `${repoKey}_${versionOnly}`.replace(/-/g, "_");
    try {
      await api.stats.click(fullImageId);
    } catch {
      // ignore click tracking errors
    }
    navigate(`/vulnerabilities/${encodeURIComponent(fullImageId)}`);
  };

  const handleSearchClick = () => {
    setActiveSearchTerm(searchInput.trim());
  };

  const fmtDate = (iso?: string | null) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toISOString().slice(0, 10);
    } catch {
      return "—";
    }
  };

  const SevRow = ({
    total,
    C,
    H,
    M,
    L,
    aria,
  }: {
    total?: number | null;
    C?: number | null;
    H?: number | null;
    M?: number | null;
    L?: number | null;
    aria: string;
  }) => (
    <div className="sevrow" role="group" aria-label={aria}>
      <span className="sev-total">
        Total: <b>{dash(total)}</b>
      </span>
      <span className="sevchip">
        <span className="sevchip-dot sev--critical" />
        <span className="sevchip-short">C</span>
        <span className="sevchip-val">{dash(C)}</span>
      </span>
      <span className="sevchip">
        <span className="sevchip-dot sev--high" />
        <span className="sevchip-short">H</span>
        <span className="sevchip-val">{dash(H)}</span>
      </span>
      <span className="sevchip">
        <span className="sevchip-dot sev--medium" />
        <span className="sevchip-short">M</span>
        <span className="sevchip-val">{dash(M)}</span>
      </span>
      <span className="sevchip">
        <span className="sevchip-dot sev--low" />
        <span className="sevchip-short">L</span>
        <span className="sevchip-val">{dash(L)}</span>
      </span>
    </div>
  );

  return (
    <>
      <div className="images-container">
        <div className="images-title-section">
          <h1 className="images-title">
            <Title />
            <p className="p-warning">
              Due to the large volume of data being processed, loading may take
              a few moments. Thank you for your patience.
            </p>
          </h1>
          <p className="images-subtitle">
            View summarized vulnerability data by image.
          </p>
        </div>
      </div>

      <div className="info-btn-div">
        <button
          className="side-info-btn"
          onClick={() => navigate("/info")}
          aria-label="Open Information"
          title="Information"
        >
          <FontAwesomeIcon icon={faCircleInfo} />
          <span className="side-info-label">Info</span>
        </button>
      </div>

      <div className="breadcrumb-div">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: repoKey, to: `/versions/${repoKey}` },
          ]}
        />
      </div>

      <div className="summaryCharts">
        {selectedImage ? (
          <SummaryCharts selectedImage={selectedImage} />
        ) : (
          <Loader />
        )}
      </div>

      <div className="summary-top-section">
        {selectedImage ? (
          <SummaryBoxChart selectedImage={selectedImage} />
        ) : (
          <Loader />
        )}
      </div>

      <div className="images-layout">
        <div className="images-left-panel">
          <div className="filter-header-l1">
            <span className="filter-header-title-l1">Filters</span>
          </div>
          <div className="images-controls">
            <select
              className="images-search-box"
              value={searchColumn}
              onChange={(e) => {
                const val = e.target.value;
                setSearchColumn(val);
                setSearchInput("");
                setActiveSearchTerm("");
              }}
            >
              <option value="">Select column…</option>
              {SEARCHABLE_COLUMNS.map((col) => (
                <option key={col.key} value={col.key}>
                  {col.label}
                </option>
              ))}
            </select>

            {searchColumn && (
              <div className="images-search-row">
                <input
                  className="images-search-box"
                  type="text"
                  placeholder={`Search in ${getColumnLabel(searchColumn)}…`}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchClick()}
                />
                <button
                  className="images-CSVButton"
                  onClick={handleSearchClick}
                >
                  Search
                </button>
              </div>
            )}
          </div>

          <div className="images-filterpanel">
            <FilterPanelLevel1 filters={filters} setFilters={setFilters} />
          </div>
        </div>

        <div className="images-right-panel">
          <div className="images-table-wrapper compact">
            <div className="table-titlebar" role="heading" aria-level={2}>
              <span className="table-title">List of Container Repositories</span>
            </div>

            <div className="table-scroll">
              <table className="images-table">
                <thead>
                  <tr>
                    <th className="th-with-q">
                      <span>Version</span>
                      <QMark label="Version help" tip={COL_DESC.version} />
                    </th>
                    <th className="th-with-q">
                      <span>Last Updated</span>
                      <QMark label="Last Updated help" tip={COL_DESC.updated} />
                    </th>
                    <th className="th-with-q">
                      <span>Layers</span>
                      <QMark label="Layers help" tip={COL_DESC.layers} />
                    </th>
                    <th className="th-with-q">
                      <span>Total Vulnerabilities</span>
                      <QMark
                        label="Total vulnerabilities help"
                        tip={COL_DESC.total}
                      />
                    </th>
                    <th className="th-with-q">
                      <span>Persisted Vulnerabilities</span>
                      <QMark
                        label="Persisted vulnerabilities help"
                        tip={COL_DESC.persisted}
                      />
                    </th>
                    <th className="th-with-q">
                      <span>Added Vulnerabilities</span>
                      <QMark
                        label="Added vulnerabilities help"
                        tip={COL_DESC.added}
                      />
                    </th>
                    <th className="th-with-q">
                      <span>Deleted Vulnerabilities</span>
                      <QMark
                        label="Deleted vulnerabilities help"
                        tip={COL_DESC.deleted}
                      />
                    </th>
                    <th className="th-with-q">
                      <span>Average Vulnerability Lifespan</span>
                      <QMark
                        label="Vulnerability lifespan help"
                        tip={COL_DESC.lifespan}
                      />
                    </th>
                    <th className="th-with-q">
                      <span>CVE Chains</span>
                      <QMark label="CVE chains help" tip={COL_DESC.chains} />
                    </th>
                    <th className="th-with-q">
                      <span>Size (MB)</span>
                      <QMark label="Size help" tip={COL_DESC.size} />
                    </th>
                    <th className="th-with-q">
                      <span>Vulnerable Packages</span>
                      <QMark
                        label="Vulnerable packages help"
                        tip={COL_DESC.pkgs}
                      />
                    </th>
                    <th className="th-with-q">
                      <span>Previous Version</span>
                      <QMark
                        label="Previous version help"
                        tip={COL_DESC.prev}
                      />
                    </th>
                    <th className="th-with-q">
                      <span>Image Digest</span>
                      <QMark
                        label="Image digest help"
                        tip={COL_DESC.digest}
                      />
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((pkg) => (
                    <tr
                      key={pkg.version}
                      onClick={() => handleRowClick(pkg.version)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="name_summary">
                        {justVersion(pkg.version)}
                      </td>
                      <td>{fmtDate(pkg.creation_time)}</td>
                      <td className="num">{dash(pkg.layer_count)}</td>
                      <td>
                        <SevRow
                          aria="Total"
                          total={pkg.total_total}
                          C={pkg.critical_total}
                          H={pkg.high_total}
                          M={pkg.medium_total}
                          L={pkg.low_total}
                        />
                      </td>
                      <td>
                        <SevRow
                          aria="Persisting"
                          total={pkg.total_persisting}
                          C={pkg.critical_persisting}
                          H={pkg.high_persisting}
                          M={pkg.medium_persisting}
                          L={pkg.low_persisting}
                        />
                      </td>
                      <td>
                        <SevRow
                          aria="Added"
                          total={pkg.total_new}
                          C={pkg.critical_new}
                          H={pkg.high_new}
                          M={pkg.medium_new}
                          L={pkg.low_new}
                        />
                      </td>
                      <td>
                        <SevRow
                          aria="Obsolete"
                          total={pkg.total_obsolete}
                          C={pkg.critical_obsolete}
                          H={pkg.high_obsolete}
                          M={pkg.medium_obsolete}
                          L={pkg.low_obsolete}
                        />
                      </td>
                      <td>
                        <SevRow
                          aria="Lifespan (days)"
                          total={pkg.lifespan_total}
                          C={pkg.lifespan_critical}
                          H={pkg.lifespan_high}
                          M={pkg.lifespan_medium}
                          L={pkg.lifespan_low}
                        />
                      </td>
                      <td className="num">{dash(pkg.total_chains)}</td>
                      <td className="num">{dash(pkg.size_mb)}</td>
                      <td className="num">{dash(pkg.package_count)}</td>
                      <td>{dash(pkg.previous_version)}</td>
                      <td className="digest-cell">{dash(pkg.digest)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="pagination-controls">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(p) =>
                    p >= 1 && p <= totalPages && setCurrentPage(p)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Summary;
